import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class EventProductionDevice extends BaseObj {
    public DeviceId: string | null;
    public __deviceid: string | null;

    public EventId: number;
    public __eventid: number;

    public Id: number;
    public __id: number;

    public IsOnline: boolean;
    public __isonline: boolean;

    public UserAgentB64: string | null;
    public __useragentb64: string | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        DeviceId?: string | null;
        EntityState?: EntityState;
        EventId?: number;
        Id?: number;
        IsOnline?: boolean;
        UserAgentB64?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<EventProductionDevice>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('EventProductionDevice tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.DeviceId = values.DeviceId ?? null;
        this.__deviceid = values.DeviceId ?? null;
        Object.defineProperty(this, 'DeviceId', {
            get(): string | null {
                return this.__deviceid;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('DeviceId', this.__deviceid, value);
                this.__deviceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventId = values.EventId ?? 0;
        this.__eventid = values.EventId ?? 0;
        Object.defineProperty(this, 'EventId', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventId', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Id = values.Id ?? 0;
        this.__id = values.Id ?? 0;
        Object.defineProperty(this, 'Id', {
            get(): number {
                return this.__id;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Id', this.__id, value);
                this.__id = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IsOnline = values.IsOnline ?? false;
        this.__isonline = values.IsOnline ?? false;
        Object.defineProperty(this, 'IsOnline', {
            get(): boolean {
                return this.__isonline;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IsOnline', this.__isonline, value);
                this.__isonline = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.UserAgentB64 = values.UserAgentB64 ?? null;
        this.__useragentb64 = values.UserAgentB64 ?? null;
        Object.defineProperty(this, 'UserAgentB64', {
            get(): string | null {
                return this.__useragentb64;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('UserAgentB64', this.__useragentb64, value);
                this.__useragentb64 = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"DeviceId": ${JSON.stringify(this.DeviceId)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventId": ${JSON.stringify(this.EventId)}` +
            ',' +
            `"Id": ${JSON.stringify(this.Id)}` +
            ',' +
            `"IsOnline": ${JSON.stringify(this.IsOnline)}` +
            ',' +
            `"UserAgentB64": ${JSON.stringify(this.UserAgentB64)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of EventProductionDevice from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        DeviceId?: string | null;
        EventId?: number;
        Id?: number;
        IsOnline?: boolean;
        UserAgentB64?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): EventProductionDevice {
        return new EventProductionDevice(values);
    }

    public static _FromServerJson(json: any): EventProductionDevice {
        return new EventProductionDevice({
            DeviceId: json.DeviceId ?? '',
            EventId: json.EventId,
            Id: json.Id,
            IsOnline: json.IsOnline,
            UserAgentB64: json.UserAgentB64 ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of EventProductionDevice from another instance of EventProductionDevice.
     * @param other The EventProductionDevice you want to clone
     * @returns A cloned EventProductionDevice
     */
    public static FromOther(other: EventProductionDevice): EventProductionDevice {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return EventProductionDevice._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return EventProductionDevice.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('DeviceId' | 'EventId' | 'Id' | 'IsOnline' | 'UserAgentB64')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'EventProductionDevice';
}
